import { ReactNode } from "react";
import classNames from "classnames";
import { OverallStatus, OverallTone, SensorHealthView, TriageBucket } from "../sensorHealth.utility";
import { SensorTriageBar } from "./SensorTriageBar";

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
  </svg>
);
const WarnIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v5m0 3h.01" />
    <circle cx="12" cy="12" r="9" />
  </svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.1}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);
const WaitIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5V12l3 2" />
  </svg>
);

const TONE: Record<OverallTone, { badge: string; headline: string; icon: ReactNode }> = {
  good: { badge: "bg-emerald-50 text-emerald-600 ring-emerald-200", headline: "text-emerald-700", icon: <CheckIcon /> },
  warn: { badge: "bg-amber-50 text-amber-600 ring-amber-200", headline: "text-amber-700", icon: <WarnIcon /> },
  bad: { badge: "bg-red-50 text-red-600 ring-red-200", headline: "text-red-700", icon: <AlertIcon /> },
  neutral: { badge: "bg-gray-100 text-gray-500 ring-gray-200", headline: "text-gray-800", icon: <WaitIcon /> },
};

type SensorHealthOverviewProps = {
  status: OverallStatus;
  counts: SensorHealthView["counts"];
  activeFilter: TriageBucket | null;
  onFilterChange: (bucket: TriageBucket | null) => void;
  live: boolean;
  pollCount: number;
};

/**
 * The page's signature element: one dominant, tone-coloured verdict that answers "is everything OK?"
 * before the technician reads anything else, with the triage counts as its supporting filters and a
 * live pulse that makes the streaming check feel alive.
 */
export const SensorHealthOverview = ({
  status,
  counts,
  activeFilter,
  onFilterChange,
  live,
  pollCount,
}: SensorHealthOverviewProps) => {
  const tone = TONE[status.tone];

  return (
    <section className="rounded-2xl border border-gray-200/80 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary-600">Sensor health</p>
        {live && (
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-700 ring-1 ring-inset ring-primary-200">
            <span className="relative flex h-2 w-2" aria-hidden>
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75 motion-reduce:hidden" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-500" />
            </span>
            Live · {pollCount} {pollCount === 1 ? "sweep" : "sweeps"}
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center gap-4">
        <span
          className={classNames(
            "flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1 ring-inset",
            tone.badge,
          )}
          aria-hidden
        >
          {tone.icon}
        </span>
        <div className="min-w-0">
          <h2 className={classNames("text-2xl font-semibold leading-tight tracking-tight sm:text-3xl", tone.headline)}>
            {status.headline}
          </h2>
          <p className="mt-1 text-sm text-gray-500">{status.detail}</p>
        </div>
      </div>

      <div className="mt-5 border-t border-gray-100 pt-4">
        <SensorTriageBar counts={counts} activeFilter={activeFilter} onFilterChange={onFilterChange} />
      </div>
    </section>
  );
};
